import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";

export const OtpDialog = ({ open, onOpenChange, channel, onVerified }) => {
  const [target, setTarget] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("target");
  const [mockCode, setMockCode] = useState("");
  const [busy, setBusy] = useState(false);
  const isSms = channel === "sms";

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { target });
      setMockCode(data.mock_code || "");
      setStep("code");
      toast.success(isSms ? "Code sent by SMS" : data.mock_code ? "Code generated" : "Code sent to your email");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/verify", { target, code });
      onVerified(data.otp_token);
      setStep("target");
      setCode("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="otp-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-2xl border-gold bg-ivory">
        <DialogHeader>
          <ShieldCheck className="mx-auto h-9 w-9 text-forest" />
          <DialogTitle className="text-center font-display text-2xl text-maroon">Quick verification</DialogTitle>
          <DialogDescription className="text-center">
            To keep voting fair, confirm your {isSms ? "phone number" : "email"} with a one-time code.
          </DialogDescription>
        </DialogHeader>
        {step === "target" ? (
          <div className="space-y-3">
            <input
              type={isSms ? "tel" : "email"}
              inputMode={isSms ? "tel" : "email"}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={isSms ? "+1 416 555 1234" : "you@example.com"}
              data-testid="otp-target-input"
              className="h-12 w-full rounded-xl border border-gold/50 bg-white px-4 text-base outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
            <button type="button" onClick={send} disabled={busy || !target} data-testid="otp-send-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-maroon font-semibold text-ivory disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send code
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {mockCode && (
              <p data-testid="otp-mock-code" className="rounded-xl border border-dashed border-saffron bg-saffron/10 p-3 text-center text-sm text-ink">
                Demo mode (no email/SMS yet) — your code is <b className="font-mono text-base tracking-widest">{mockCode}</b>
              </p>
            )}
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-code-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-11 bg-white text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button type="button" onClick={verify} disabled={busy || code.length !== 6} data-testid="otp-verify-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-semibold text-white disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & submit votes
            </button>
            <button type="button" onClick={() => setStep("target")} data-testid="otp-change-target-btn" className="w-full text-sm text-ink-muted underline">
              Use a different {isSms ? "number" : "email"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
